import styled, { keyframes } from 'styled-components';

// Implementacao do design Maquinas.html — hairline shell pattern:
// page-header + filters 4-col + table 4-col grid + pager.
//
// Mantemos exports legados (Wrapper, ButtonEstacao, ButtonDiv,
// IconNew, LabelCadastro, PaginationContainer, Label) como
// compat-aliases caso algo importe daqui de fora.

// ============================================================
// Container
// ============================================================
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 64px);
  min-height: 100vh;
  margin: 0 0 0 64px;
  padding: 0;
  box-sizing: border-box;
  background: var(--cco-bg);
  color: var(--cco-ink);

  @media (max-width: 720px) {
    width: 100vw;
    margin: 0;
  }
`;

// ============================================================
// Page Header (.ph) — titulo + sub + contador "X máquinas"
// ============================================================
export const PageHeader = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

export const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--cco-ink);
`;

export const PageSub = styled.div`
  color: var(--cco-ink-mute);
  font-size: 13px;
  margin-top: 3px;
`;

export const PageCount = styled.div`
  font-size: 13px;
  color: var(--cco-ink-mute);
  font-variant-numeric: tabular-nums;

  b {
    color: var(--cco-ink);
    font-weight: 700;
  }
`;

// ============================================================
// Filters (.filters) — grid 4-col com hairlines
// busca | cliente | estacao | CTA
// ============================================================
export const Filters = styled.section`
  display: grid;
  grid-template-columns: 1fr 240px 240px auto;
  border-bottom: 1px solid var(--cco-line);

  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
    > * + * {
      border-left: 0;
    }
    > *:nth-child(2n) {
      border-right: 0;
    }
    > *:not(:nth-child(2n)) {
      border-right: 1px solid var(--cco-line);
    }
    > *:not(:nth-child(-n + 2)) {
      border-top: 1px solid var(--cco-line);
    }
    > .cta {
      grid-column: 1 / -1;
      border-right: 0;
    }
  }

  /* Mobile: tudo empilhado em 1 coluna, hairlines viram bottom-borders */
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    > * {
      border: 0 !important;
      border-bottom: 1px solid var(--cco-line) !important;
    }
    > *:last-child {
      border-bottom: 0 !important;
    }
  }
`;

// Search cell — lupa svg + input transparente
export const Search = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px var(--cco-pad);

  svg {
    color: var(--cco-ink-mute);
    flex: none;
    width: 16px;
    height: 16px;
  }

  input {
    flex: 1;
    background: transparent;
    border: 0;
    outline: 0;
    color: var(--cco-ink);
    font: inherit;
    font-size: 14px;
    padding: 8px 0;
    min-width: 0;

    ::placeholder {
      color: var(--cco-ink-faint);
    }
  }
`;

// Sel cell — opcional label .k (uppercase) + native select com seta SVG
export const SelCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px var(--cco-pad);

  .k {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--cco-ink-mute);
    flex: none;
  }

  select {
    flex: 1;
    background: transparent;
    border: 0;
    outline: 0;
    color: var(--cco-ink);
    font: inherit;
    font-size: 13.5px;
    font-weight: 500;
    padding: 8px 22px 8px 0;
    appearance: none;
    cursor: pointer;
    width: 100%;
    min-width: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='rgba(255,255,255,.4)' d='M0 0h12L6 8z'/></svg>");
    background-repeat: no-repeat;
    background-position: right 0 center;
  }

  select option {
    background: var(--cco-bg);
    color: var(--cco-ink);
  }
`;

// Cta button .cta — pill cyan-soft com .ic (square solid) + .tx (dual line)
export const Cta = styled.button.attrs({ className: 'cta' })`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: rgba(28, 176, 216, 0.1);
  color: var(--cco-cyan);
  border: 0;
  transition: background 150ms ease;
  cursor: pointer;
  min-width: 240px;
  font-family: inherit;

  @media (max-width: 720px) {
    min-width: 0;
    width: 100%;
    justify-content: flex-start;
  }

  :hover {
    background: rgba(28, 176, 216, 0.18);
  }

  .ic {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: var(--cco-cyan);
    color: #0a0a0b;
    display: grid;
    place-items: center;
    flex: none;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .tx {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.25;
  }
  .tx .t {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .tx .s {
    font-size: 11.5px;
    color: var(--cco-ink-mute);
    font-weight: 500;
  }
`;

// ============================================================
// Table (.table) — grid 4-col: Maquina | Cliente | Estacao | Editar
// ============================================================
export const TableWrap = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const Table = styled.div`
  display: grid;
  /* $admin=true habilita 2 colunas extras (Status + Ativacao) so pra
     higra/admin. Operadores comuns nao precisam ver status pois nao
     enxergam maquinas desativadas. */
  /* Colunas: Editar | Maquina | Serie | Cliente | Estacao | Atualizacao |
     [Status] | [Ativacao]. Editar primeiro (acao rapida). A coluna
     Atualizacao (175px) cabe "dd/MM/yyyy - HH:mm" em uma linha. */
  grid-template-columns: ${(props) =>
    props.$admin
      ? '90px 1.15fr 140px 1fr 1.15fr 175px 110px 110px'
      : '90px 1.2fr 140px 1fr 1.2fr 175px'};
  align-items: stretch;

  /* Mobile: vira card layout. Header rows ficam ocultos (display:none),
     cada Tr vira um "card" com filhos empilhados. Cada Tc tem ::before
     com data-label exibindo o titulo da coluna inline. */
  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
  }
`;

export const Th = styled.div`
  padding: 14px var(--cco-pad);
  color: var(--cco-ink-mute);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--cco-line);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  /* Em card layout (mobile), labels viram inline dentro de cada cell —
     o Th original some pra nao duplicar. */
  @media (max-width: 720px) {
    display: none;
  }
`;

// Tr — display:contents pra cada filho ir direto pro grid pai.
// Em mobile vira card com bordas e gap.
export const Tr = styled.div`
  display: contents;

  :hover .tc {
    background: rgba(255, 255, 255, 0.02);
  }

  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
    padding: 14px var(--cco-pad);
    border-bottom: 1px solid var(--cco-line);
    gap: 4px;

    /* Como cada Tr vira um card, o Tc nao precisa mais de border-bottom
       individual. Cancelamos via override abaixo. */
  }
`;

export const Tc = styled.div.attrs({ className: 'tc' })`
  padding: 18px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
  min-width: 0;
  gap: 8px;

  @media (max-width: 720px) {
    padding: 4px 0;
    border-bottom: 0;
    justify-content: flex-start;
    /* Label inline (data-label do JSX OU sobrescrito pelas variantes
       TcMaquina/TcCliente/TcEstacao abaixo). */
    &::before {
      content: attr(data-label);
      color: var(--cco-ink-mute);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      margin-right: 8px;
      min-width: 70px;
      flex-shrink: 0;
    }
  }
`;

export const TcMaquina = styled(Tc).attrs({ 'data-label': 'Máquina' })`
  font-weight: 700;
  color: var(--cco-ink);
  font-size: 14px;
  letter-spacing: 0.02em;
`;

export const TcSerial = styled(Tc).attrs({ 'data-label': 'Série' })`
  color: var(--cco-ink-mute);
  font-size: 12.5px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

export const TcCliente = styled(Tc).attrs({ 'data-label': 'Cliente' })`
  font-weight: 600;
  color: var(--cco-ink);
  font-size: 13.5px;
  letter-spacing: 0.02em;
`;

export const TcEstacao = styled(Tc).attrs({ 'data-label': 'Estação' })`
  color: var(--cco-ink);
  font-size: 13.5px;
`;

// Ultima atualizacao de dados — "DD/MM/AAAA - HH:MM" em UMA linha so
// (nowrap). Fonte menor + tabular-nums pra caber na coluna sem quebrar.
export const TcAtualizacao = styled(Tc).attrs({ 'data-label': 'Atualização' })`
  color: var(--cco-ink-mute);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  @media (max-width: 720px) {
    white-space: normal;
  }
`;

// Row actions wrapper (para futura adicao de mais botoes na linha)
export const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

// Linha desativada — fade sutil nas celulas de dados. O StatusCell ja
// comunica "Inativo" via cor + label, entao nao precisa strikethrough.
// Coluna Ativacao + Editar ficam em opacidade cheia pra toggle continuar
// clicavel sem ruido visual.
export const InactiveRow = styled.div`
  display: contents;

  > .tc:nth-child(-n + 3) {
    opacity: 0.55;
  }

  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
    padding: 14px var(--cco-pad);
    border-bottom: 1px solid var(--cco-line);
    gap: 4px;

    > .tc:not(:last-child):not(:nth-last-child(2)) {
      opacity: 0.55;
    }
  }
`;

// StatusCell — bolinha colorida + label ("Ativo"/"Inativo"). Verde
// com glow quando ativa, cinza+vermelho quando inativa. Visualmente
// igual aos status pills usados em outras telas do sistema.
export const StatusCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) =>
    props.$active ? 'var(--cco-green)' : 'var(--cco-ink-mute)'};
  opacity: 1 !important;
  text-decoration: none !important;

  ::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) =>
      props.$active ? 'var(--cco-green)' : 'var(--cco-red)'};
    box-shadow: ${(props) =>
      props.$active
        ? '0 0 8px rgba(43, 212, 125, 0.55)'
        : '0 0 6px rgba(244, 68, 92, 0.35)'};
    flex: none;
  }
`;

// ToggleSwitch — pill switch padrao (44x24). Verde quando ativo,
// cinza quando inativo. Bolinha branca desliza ao alternar. Disabled
// (ex.: durante PATCH) reduz opacidade e bloqueia ponteiro.
export const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex: none;
  opacity: 1 !important;
  text-decoration: none !important;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  .slider {
    position: absolute;
    inset: 0;
    background: var(--cco-line-strong);
    border-radius: 999px;
    transition: background 180ms ease;

    ::before {
      content: '';
      position: absolute;
      left: 3px;
      top: 50%;
      transform: translateY(-50%);
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #fff;
      transition: transform 180ms ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    }
  }

  input:checked + .slider {
    background: var(--cco-green);
  }

  input:checked + .slider::before {
    transform: translate(20px, -50%);
  }

  input:disabled + .slider {
    opacity: 0.55;
    cursor: not-allowed;
  }

  :hover input:not(:disabled) + .slider {
    filter: brightness(1.08);
  }
`;

// AdminBar — barra fina abaixo dos filtros, exibida apenas pra higra/admin.
// Hoje contem so o toggle "Mostrar desativadas"; espaco pra futuras
// ferramentas administrativas (ex.: exportar lista, ativar em lote).
export const AdminBar = styled.section`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  background: var(--cco-soft-surface);

  @media (max-width: 720px) {
    justify-content: flex-start;
    padding: 0 var(--cco-pad);
  }
`;

// Toggle "Mostrar desativadas" — checkbox custom + label.
// So aparece pra higra/admin (gated no JSX da tela).
export const ShowInactiveToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px var(--cco-pad);
  color: var(--cco-ink-mute);
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  user-select: none;

  input {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid var(--cco-line-strong);
    background: transparent;
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: all 150ms ease;
    margin: 0;
    flex: none;
  }

  input:checked {
    background: var(--cco-cyan);
    border-color: var(--cco-cyan);
  }

  input:checked::after {
    content: '';
    width: 8px;
    height: 4px;
    border-left: 2px solid #06151a;
    border-bottom: 2px solid #06151a;
    transform: rotate(-45deg) translate(1px, -1px);
  }

  :hover input:not(:checked) {
    border-color: var(--cco-ink-mute);
  }
`;

// IconBtn — 36x32 com borda (estilo Maquinas.html). Hover cyan-soft.
export const IconBtn = styled.button`
  width: 36px;
  height: 32px;
  /* Touch target >=44px no mobile (WCAG 2.5.5 / Apple HIG). min-* vence
     o width/height fixo quando >=, sem alterar o tamanho desktop. */
  @media (max-width: 720px) {
    min-width: 44px;
    min-height: 44px;
  }
  border-radius: 8px;
  background: transparent;
  border: 1px solid var(--cco-line-strong);
  display: grid;
  place-items: center;
  color: var(--cco-ink-mute);
  transition: all 150ms ease;
  cursor: pointer;
  font-family: inherit;

  :hover:not(:disabled) {
    color: var(--cco-cyan);
    border-color: var(--cco-cyan);
    background: rgba(28, 176, 216, 0.08);
  }

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Empty row (full-width across all 4 cols)
export const EmptyRow = styled.div`
  grid-column: 1 / -1;
  padding: 60px 0;
  text-align: center;
  color: var(--cco-ink-faint);
`;

// Skeleton row pra loading
const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const SkeletonCell = styled(Tc)`
  ::before {
    content: '';
    display: block;
    width: ${(props) => props.$w || '70%'};
    height: 14px;
    border-radius: 6px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.08) 50%,
      rgba(255, 255, 255, 0.04) 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }

  /* Mobile: cancela a regra label do Tc base (::before fica como
     shimmer bar, nao como rotulo de coluna). Skeleton tambem nao
     precisa de min-width fixo. */
  @media (max-width: 720px) {
    ::before {
      margin-right: 0;
      min-width: 0;
    }
  }
`;

// ============================================================
// Pager (.pager) — Anterior / 1 2 3 ... n / Proximo + exibir N
// ============================================================
export const Pager = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 20px var(--cco-pad);
  border-top: 1px solid var(--cco-line);
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 8px;
    padding: 14px var(--cco-pad);

    .btn {
      padding: 0 12px;
      font-size: 11.5px;
    }
    .pg {
      width: 32px;
      height: 32px;
      font-size: 12.5px;
    }
  }

  .btn {
    height: 36px;
    padding: 0 18px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--cco-line);
    color: var(--cco-ink);
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    transition: all 150ms ease;
    cursor: pointer;
    font-family: inherit;
  }
  .btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  .btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .pages {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .pg {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--cco-line);
    color: var(--cco-ink);
    font-size: 13.5px;
    font-weight: 600;
    transition: all 150ms ease;
    cursor: pointer;
    font-family: inherit;
  }
  .pg.on {
    background: var(--cco-cyan);
    border-color: var(--cco-cyan);
    color: #0a0a0b;
  }
  .pg:not(.on):hover {
    background: rgba(255, 255, 255, 0.08);
  }
  .dots {
    color: var(--cco-ink-mute);
    padding: 0 4px;
    font-weight: 700;
  }
  .exhibit {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--cco-ink-mute);
    font-size: 12.5px;
  }
  .exhibit select {
    height: 32px;
    padding: 0 28px 0 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--cco-line);
    color: var(--cco-ink);
    font-size: 13px;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(255,255,255,.4)' d='M0 0h10L5 6z'/></svg>");
    background-repeat: no-repeat;
    background-position: right 8px center;
    font-family: inherit;
  }
`;

// ============================================================
// Compat exports — antigos consumers fora desta tela.
// ============================================================
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const PaginationContainer = Pager;

export const Label = styled.span`
  font-size: 12.5px;
  color: var(--cco-ink-mute);
`;

export const ButtonEstacao = Cta;
export const ButtonDiv = styled.div``;
export const IconNew = styled.img``;
export const LabelCadastro = styled.span``;
